import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { apiConstants, getGeneric } from './constants'

/**
 * Generic reasons dropdown backed by /api/reason-master. Pick reasons by
 * `category` (e.g. `RTO_REQUEST`). Items are sorted by `displayOrder`
 * (ascending), then by label as a tiebreaker.
 *
 * @param {string}   category     Reason category to fetch (e.g. "RTO_REQUEST").
 * @param {string}   value        Currently selected reason `code`.
 * @param {function} onChange     Called with the selected `code` string.
 * @param {string=}  label        Field label. Defaults to "Reason".
 * @param {boolean=} required     Show required asterisk.
 * @param {string=}  error        Error message; renders red helper text + outline.
 * @param {boolean=} disabled
 * @param {boolean=} activeOnly   When true (default), only fetches active items.
 * @param {object=}  sx           Forwarded to the FormControl.
 */
export default function ReasonDropdown({
  category,
  value,
  onChange,
  label = 'Reason',
  required = false,
  error,
  disabled = false,
  activeOnly = true,
  sx,
}) {
  const { enqueueSnackbar } = useSnackbar()
  const [reasons, setReasons] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!category) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await getGeneric(apiConstants.REASON_MASTER, {
          category,
          activeOnly: activeOnly ? 'true' : 'false',
        })
        const list = Array.isArray(res?.data) ? res.data : res?.data?.responseObj || []
        if (!cancelled) setReasons(list)
      } catch (e) {
        if (!cancelled) {
          setReasons([])
          enqueueSnackbar(
            e?.response?.data?.message || 'Failed to load reasons',
            { variant: 'error' },
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [category, activeOnly, enqueueSnackbar])

  const sorted = useMemo(() => {
    return [...reasons].sort((a, b) => {
      const ao = a.displayOrder == null ? Number.MAX_SAFE_INTEGER : a.displayOrder
      const bo = b.displayOrder == null ? Number.MAX_SAFE_INTEGER : b.displayOrder
      if (ao !== bo) return ao - bo
      return (a.label || '').localeCompare(b.label || '')
    })
  }, [reasons])

  const inputId = `reason-dropdown-${category}`

  return (
    <FormControl fullWidth size='small' error={Boolean(error)} disabled={disabled} sx={sx}>
      <InputLabel id={`${inputId}-label`} required={required}>
        {label}
      </InputLabel>
      <Select
        labelId={`${inputId}-label`}
        id={inputId}
        label={label}
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
      >
        {loading && (
          <MenuItem value='' disabled>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} /> Loading…
            </Box>
          </MenuItem>
        )}
        {!loading && sorted.length === 0 && (
          <MenuItem value='' disabled>
            No reasons configured
          </MenuItem>
        )}
        {sorted.map((r) => (
          <MenuItem key={r.id || r.code} value={r.code}>
            {r.label}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  )
}
